import dayjs from './dayjs';

/**
 * Marketplace locale (a key of `config.languages`) -> moment/dayjs locale id.
 *
 * 'no' maps to 'nn' because neither moment nor dayjs ships a 'no' locale.
 * 'en' needs no mapping entry beyond itself — both libraries have it built in,
 * so there is no locale file to load for it.
 */
const LOCALE_IDS = {
  de: 'de',
  en: 'en',
  es: 'es',
  fr: 'fr',
  it: 'it',
  no: 'nn',
  sv: 'sv',
};

/**
 * Loads the moment locale file for `id`. Each of these modules registers itself
 * on the moment singleton as a side effect, so the lazy chunks that still
 * import moment statically (ManageAvailabilityCalendar, PassCard,
 * StoryblokChangedAt, MUI's AdapterMoment) see the same, correctly-locked
 * instance — webpack dedupes moment across chunks.
 *
 * The explicit switch (rather than a template literal) keeps webpack from
 * emitting a chunk for every locale moment ships.
 */
const loadMomentLocale = (id) => {
  switch (id) {
    case 'de':
      return import(/* webpackChunkName: "moment-locale-de" */ 'moment/locale/de');
    case 'es':
      return import(/* webpackChunkName: "moment-locale-es" */ 'moment/locale/es');
    case 'fr':
      return import(/* webpackChunkName: "moment-locale-fr" */ 'moment/locale/fr');
    case 'it':
      return import(/* webpackChunkName: "moment-locale-it" */ 'moment/locale/it');
    case 'nn':
      return import(/* webpackChunkName: "moment-locale-nn" */ 'moment/locale/nn');
    case 'sv':
      return import(/* webpackChunkName: "moment-locale-sv" */ 'moment/locale/sv');
    default:
      // 'en' is built into moment core.
      return Promise.resolve();
  }
};

let momentLocaleReady = Promise.resolve();

/**
 * Returns a promise that settles once the moment locale requested by the most
 * recent `applyDateLocale` call has been applied.
 *
 * SSR must await this before rendering: lazy components are resolved
 * synchronously on the server, so a PassCard formatting `moment.localeData()`
 * would otherwise render with the wrong locale and break hydration.
 *
 * @returns {Promise<void>}
 */
export const whenMomentLocaleReady = () => momentLocaleReady;

/**
 * Applies the marketplace locale to both date libraries.
 *
 * dayjs (main bundle) is set synchronously; moment (code-split chunks only) is
 * set asynchronously so that neither moment nor its locale data is pulled back
 * into the main bundle.
 *
 * @param {string} locale a key of `config.languages`, e.g. 'de'
 * @returns {Promise<void>} resolves once the moment locale is applied
 */
export const applyDateLocale = (locale) => {
  const id = LOCALE_IDS[locale];
  // Unknown locale: leave both libraries on their current locale, which is what
  // `moment.locale('xx')` did before.
  if (!id) return momentLocaleReady;

  dayjs.locale(id);

  momentLocaleReady = Promise.all([
    import(/* webpackChunkName: "moment" */ 'moment'),
    loadMomentLocale(id),
  ])
    .then(([momentModule]) => {
      const moment = momentModule.default || momentModule;
      moment.locale(id);
    })
    .catch(() => {
      // A failed chunk load must not take the app down — moment keeps its
      // default ('en') locale and only date formatting inside lazy chunks is
      // affected.
    });

  return momentLocaleReady;
};
